import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import LoadingSpinner from "../LoadingSpinner";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isToday,
  isAfter,
  isBefore,
  eachDayOfInterval,
  getDay,
} from "date-fns";
import { ja } from "date-fns/locale";
import { utcToZonedTime } from "date-fns-tz";
import { CalendarProps, CalendarDay } from "./Calendar.types";
import { useTheme } from "../../../theme/ThemeProvider";
import { useTranslation } from "react-i18next";

/**
 * Pure UI Calendar Component
 *
 * A themeable calendar that only handles UI rendering and user interactions.
 * Business logic like availability is handled by parent components.
 */
const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateSelect,
  onMonthChange,
  isDateSelectable,
  getDayClassNames,
  minDate,
  maxDate,
  dayRenderer,
  showNavigation = true,
  className = "",
  isLoading = false,
  disabledDates = [],
  disabledDays = [],
  highlightedDates = [],
  timezone,
  initialMonth,
  compact = false,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  // Track whether user has manually navigated (prev/next month buttons)
  // Once set, subsequent initialMonth changes from async data are ignored
  const hasUserNavigated = useRef(false);

  // Priority: initialMonth > selectedDate > new Date()
  const [currentMonth, setCurrentMonth] = useState(
    initialMonth || (selectedDate ? new Date(selectedDate) : new Date())
  );

  // FIXED: Create timezone-aware isToday function
  const isTodayInTimezone = useCallback((date: Date): boolean => {
    if (!timezone) {
      // Fallback to regular isToday when no timezone specified
      return isToday(date);
    }

    // Get current time in the specified timezone
    const nowInTimezone = utcToZonedTime(new Date(), timezone);
    const dateInTimezone = utcToZonedTime(date, timezone);

    // Compare dates ignoring time
    return (
      nowInTimezone.getFullYear() === dateInTimezone.getFullYear() &&
      nowInTimezone.getMonth() === dateInTimezone.getMonth() &&
      nowInTimezone.getDate() === dateInTimezone.getDate()
    );
  }, [timezone]);

  // Update current month when selected date is first set (one-time sync, not reactive)
  useEffect(() => {
    if (selectedDate && !isSameMonth(selectedDate, currentMonth)) {
      setCurrentMonth(new Date(selectedDate));
    }
  }, [selectedDate]); // Removed currentMonth from dependencies to prevent reactive updates

  // Update current month when initialMonth changes (e.g., after prefetch or timezone switch)
  // Skip if the user has manually navigated — their navigation takes priority
  useEffect(() => {
    if (initialMonth) {
      if (!hasUserNavigated.current && !isSameMonth(initialMonth, currentMonth)) {
        setCurrentMonth(new Date(initialMonth));
      }
    } else {
      // initialMonth was reset to null (timezone change, provider change, cache clear)
      // Allow next non-null initialMonth to take effect again
      hasUserNavigated.current = false;
    }
  }, [initialMonth]); // Only react to initialMonth changes

  // Navigate to previous month
  const prevMonth = useCallback(() => {
    hasUserNavigated.current = true;
    const newMonth = subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  }, [currentMonth, onMonthChange]);

  // Navigate to next month
  const nextMonth = useCallback(() => {
    hasUserNavigated.current = true;
    const newMonth = addMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  }, [currentMonth, onMonthChange]);

  // Check if a date is selectable
  const checkDateSelectable = useCallback(
    (date: Date): boolean => {
      // Use provided function if available
      if (isDateSelectable) {
        return isDateSelectable(date);
      }

      // Default logic: date must be today or in the future
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Check min date constraint
      if (minDate && isBefore(date, minDate)) {
        return false;
      }

      // Check max date constraint
      if (maxDate && isAfter(date, maxDate)) {
        return false;
      }

      // Check if date is not before today
      return !isBefore(date, today);
    },
    [isDateSelectable, minDate, maxDate]
  );

  // Generate days for the current month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days: CalendarDay[] = [];
    let day = startDate;

    while (day <= endDate) {
      const isSelectable = checkDateSelectable(day);

      days.push({
        date: new Date(day.getFullYear(), day.getMonth(), day.getDate(), 12, 0, 0, 0),
        isSelectable,
        isCurrentMonth: isSameMonth(day, monthStart),
        isSelected: selectedDate ? isSameDay(day, selectedDate) : false,
        isToday: isTodayInTimezone(day),
      });

      day = addDays(day, 1);
    }

    return days;
  }, [currentMonth, selectedDate, checkDateSelectable]);

  // Group days by week for rendering
  const calendarWeeks = useMemo(() => {
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }
    return weeks;
  }, [calendarDays]);

  // Handle day click
  const handleDayClick = useCallback(
    (day: CalendarDay) => {
      if (day.isSelectable) {
        onDateSelect(day.date);
      }
    },
    [onDateSelect]
  );

  // Default day renderer
  const defaultDayRenderer = useCallback((day: CalendarDay) => {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center relative">
        <span>{format(day.date, "d")}</span>
        {/* Brand primary, not Tailwind's blue-600 — this dot sits next to
            brand-coloured selection states in every picker that uses this
            calendar, and two nearly-identical blues read as a mistake. */}
        {day.isToday && !day.isSelected && (
          <div className="absolute bottom-0 w-1.5 h-1.5 bg-primary-600 rounded-full"></div>
        )}
      </div>
    );
  }, []);

  // Generate class names for a day cell
  const getDayCellClassNames = useCallback(
    (day: CalendarDay) => {
      // Base class for all day cells
      const classes = [
        "relative w-10 h-12 flex items-center justify-center text-sm rounded-full mx-auto",
        "!transition-none", // Force no transitions
      ];

      // Add conditional classes
      if (day.isCurrentMonth && !day.isSelected && !day.isToday) {
        classes.push("text-gray-600");
      }

      if (!day.isCurrentMonth) {
        classes.push("text-gray-400");
      }

      // Selection styling is handled by the custom day renderer

      // Today styling is now handled in the day renderer with a blue dot

      // Remove background hover effects since selection is handled by custom renderer

      if (day.isSelectable) {
        classes.push("cursor-pointer");
      } else {
        classes.push("cursor-not-allowed opacity-50");
      }

      // Add custom class names if provided
      if (getDayClassNames) {
        classes.push(getDayClassNames(day));
      }

      return classes.filter(Boolean).join(" ");
    },
    [getDayClassNames]
  );

  // Format day names
  const dayNames = useMemo(() => {
    return [
      t('calendar.weekdays.sunday'),
      t('calendar.weekdays.monday'),
      t('calendar.weekdays.tuesday'),
      t('calendar.weekdays.wednesday'),
      t('calendar.weekdays.thursday'),
      t('calendar.weekdays.friday'),
      t('calendar.weekdays.saturday')
    ];
  }, [t]);

  // Get days in current month
  const daysInMonth = useMemo(() => {
    const firstDayOfMonth = startOfMonth(currentMonth);
    const lastDayOfMonth = endOfMonth(currentMonth);

    return eachDayOfInterval({
      start: firstDayOfMonth,
      end: lastDayOfMonth,
    });
  }, [currentMonth]);

  // Calculate blank spaces at start of month
  const blanks = useMemo(() => {
    const firstDayOfMonth = startOfMonth(currentMonth);
    const firstDayOfWeek = getDay(firstDayOfMonth);
    return Array(firstDayOfWeek).fill(null);
  }, [currentMonth]);

  // Calculate total days (including blanks)
  const totalDays = useMemo(() => {
    return [...blanks, ...daysInMonth];
  }, [blanks, daysInMonth]);

  // Check if a date is disabled
  const isDisabled = useCallback(
    (date: Date) => {
      // Check min date
      if (minDate && date < minDate) return true;

      // Check max date
      if (maxDate && date > maxDate) return true;

      // Check disabled specific dates
      if (disabledDates.some((disabledDate) => isSameDay(date, disabledDate)))
        return true;

      // Check disabled days of week
      if (disabledDays.includes(getDay(date))) return true;

      return false;
    },
    [minDate, maxDate, disabledDates, disabledDays]
  );

  // Check if a date is highlighted
  const isHighlighted = useCallback(
    (date: Date) => {
      return highlightedDates.some((highlightedDate) =>
        isSameDay(date, highlightedDate)
      );
    },
    [highlightedDates]
  );

  // Handle date click
  const handleDateClick = useCallback(
    (date: Date) => {
      if (!isDisabled(date)) {
        onDateSelect(date);
      }
    },
    [isDisabled, onDateSelect]
  );

  // Generate day class based on state
  const getDayClass = useCallback(
    (day: Date | null) => {
      if (!day) return "";

      // Base styles for all days
      let baseClasses =
        "h-8 w-8 flex items-center justify-center rounded-full text-sm";

      // If day is disabled
      if (isDisabled(day)) {
        return `${baseClasses} text-[${theme.calendar.disabledText}] cursor-not-allowed`;
      }

      // If day is selected
      if (selectedDate && isSameDay(day, selectedDate)) {
        return `${baseClasses} bg-[${theme.calendar.selectedBackground}] text-[${theme.calendar.selectedText}] hover:bg-[${theme.calendar.selectedHoverBackground}]`;
      }

      // If day is today
      if (isTodayInTimezone(day)) {
        return `${baseClasses} border border-[${theme.colors.primary}] text-[${theme.colors.primary}] hover:bg-[${theme.calendar.dayHoverBackground}]`;
      }

      // If day is highlighted
      if (isHighlighted(day)) {
        return `${baseClasses} bg-[${theme.calendar.highlightedBackground}] text-[${theme.calendar.highlightedText}] hover:bg-[${theme.calendar.dayHoverBackground}]`;
      }

      // If day is not in current month
      if (!isSameMonth(day, currentMonth)) {
        return `${baseClasses} text-[${theme.calendar.outsideMonthText}] hover:bg-[${theme.calendar.dayHoverBackground}]`;
      }

      // Default day style
      return `${baseClasses} hover:bg-[${theme.calendar.dayHoverBackground}] cursor-pointer`;
    },
    [currentMonth, isDisabled, isHighlighted, selectedDate, theme]
  );

  // Get date-fns locale based on i18n language
  const dateFnsLocale = useMemo(() => {
    return t('common:languages.ja') && i18n.language === 'ja' ? ja : undefined;
  }, [t, i18n.language]);

  // Format month header with proper locale
  const monthHeader = useMemo(() => {
    const locale = i18n.language;
    if (locale === 'ja') {
      // Japanese format: "2025年1月"
      return format(currentMonth, 'yyyy年M月', { locale: ja });
    } else {
      // English format: "January 2025"
      return format(currentMonth, 'MMMM yyyy');
    }
  }, [currentMonth, i18n.language]);

  return (
    <div className={`calendar ${className}`}>
      {/* Calendar header with month/year and navigation */}
      <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-4'}`}>
        <h2 className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-lg'}`}>
          {monthHeader}
        </h2>

        {showNavigation && (
          <div className="flex space-x-0.5">
            <button
              type="button"
              onClick={prevMonth}
              className={`rounded-md text-gray-700 hover:bg-gray-100 ${compact ? 'p-1' : 'p-2'}`}
              aria-label={t('calendar.navigation.previousMonth')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={compact ? 'h-4 w-4' : 'h-5 w-5'}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={nextMonth}
              className={`rounded-md text-gray-700 hover:bg-gray-100 ${compact ? 'p-1' : 'p-2'}`}
              aria-label={t('calendar.navigation.nextMonth')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={compact ? 'h-4 w-4' : 'h-5 w-5'}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
          <LoadingSpinner size="lg" color="brand" />
        </div>
      )}

      {/* Weekday headers */}
      <div className={`grid grid-cols-7 ${compact ? 'gap-0 mb-1' : 'gap-1 mb-2'}`}>
        {dayNames.map((day, index) => (
          <div
            key={index}
            className={`text-center font-medium text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="relative">
        {calendarWeeks.map((week, weekIndex) => (
          <div key={weekIndex} className={`grid grid-cols-7 ${compact ? 'gap-0 mb-0' : 'gap-1 mb-1'}`}>
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                onClick={() => day.isSelectable && handleDateClick(day.date)}
                className={`${getDayCellClassNames(day)} ${compact ? '!w-8 !h-8 text-xs' : ''}`}
                style={{ transition: 'none !important' }}
              >
                {dayRenderer ? dayRenderer(day) : defaultDayRenderer(day)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
